import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

export type PaperStyle = 'lined' | 'plain';
export type FontFamily = 'roboto' | 'georgia' | 'arial' | 'times' | 'courier';
export type PinSessionDuration = 0 | 5 | 15 | 30 | 60; // minutes, 0 = always ask
export type LineSpacing = 24 | 28 | 32 | 36 | 40; // pixels between lines

interface Settings {
  paperStyle: PaperStyle;
  textPaddingTop: number;
  fontSize: number;
  fontFamily: FontFamily;
  lineOpacity: number;
  marginLineOpacity: number;
  lineSpacing: LineSpacing;
  // Security
  pinCode: string | null;
  pinSessionMinutes: PinSessionDuration; // How long to stay unlocked after entering PIN
}

interface SettingsStore extends Settings {
  isLoading: boolean;
  userId: string | null;
  // Session-only state (not persisted to cloud)
  pinSessionStart: number | null; // Timestamp when PIN was last entered
  
  // Actions
  setPaperStyle: (style: PaperStyle) => void;
  setTextPaddingTop: (padding: number) => void;
  setFontSize: (size: number) => void;
  setFontFamily: (font: FontFamily) => void;
  setLineOpacity: (opacity: number) => void;
  setMarginLineOpacity: (opacity: number) => void;
  setLineSpacing: (spacing: LineSpacing) => void;
  setPinCode: (pin: string | null) => void;
  setPinSessionMinutes: (minutes: PinSessionDuration) => void;
  startPinSession: () => void;
  isPinSessionValid: () => boolean;
  clearPinSession: () => void;
  resetToDefaults: () => void;
  
  // Cloud sync
  setUserId: (userId: string | null) => void;
  loadFromCloud: () => Promise<void>;
  saveToCloud: () => Promise<void>;
}

const defaultSettings: Settings = {
  paperStyle: 'lined',
  textPaddingTop: 2,
  fontSize: 16,
  fontFamily: 'roboto',
  lineOpacity: 40,
  marginLineOpacity: 60,
  lineSpacing: 32,
  pinCode: null,
  pinSessionMinutes: 5,
};

// Font family CSS values
export const fontFamilyMap: Record<FontFamily, string> = {
  roboto: "'Roboto', sans-serif",
  georgia: "'Georgia', serif",
  arial: "'Arial', sans-serif",
  times: "'Times New Roman', serif",
  courier: "'Courier New', monospace",
};

// Font display names
export const fontDisplayNames: Record<FontFamily, string> = {
  roboto: 'Roboto',
  georgia: 'Georgia',
  arial: 'Arial',
  times: 'Times New Roman',
  courier: 'Courier New',
};

// PIN session duration options
export const pinSessionOptions: { value: PinSessionDuration; label: string }[] = [
  { value: 0, label: 'Always ask' },
  { value: 5, label: '5 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
];

// Line spacing options
export const lineSpacingOptions: { value: LineSpacing; label: string }[] = [
  { value: 24, label: 'Compact' },
  { value: 28, label: 'Tight' },
  { value: 32, label: 'Normal' },
  { value: 36, label: 'Relaxed' },
  { value: 40, label: 'Spacious' },
];

// Firestore collection for user settings
const SETTINGS_COLLECTION = 'userSettings';

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set, get) => ({
      ...defaultSettings,
      isLoading: false,
      userId: null,
      pinSessionStart: null,

      setPaperStyle: (style) => {
        set({ paperStyle: style });
        get().saveToCloud();
      },
      
      setTextPaddingTop: (padding) => {
        set({ textPaddingTop: padding });
        get().saveToCloud();
      },
      
      setFontSize: (size) => {
        set({ fontSize: size });
        get().saveToCloud();
      },

      setFontFamily: (font) => {
        set({ fontFamily: font });
        get().saveToCloud();
      },

      setLineOpacity: (opacity) => {
        set({ lineOpacity: opacity });
        get().saveToCloud();
      },

      setMarginLineOpacity: (opacity) => {
        set({ marginLineOpacity: opacity });
        get().saveToCloud();
      },

      setLineSpacing: (spacing) => {
        set({ lineSpacing: spacing });
        get().saveToCloud();
      },

      setPinCode: (pin) => {
        set({ pinCode: pin, pinSessionStart: null });
        get().saveToCloud();
      },

      setPinSessionMinutes: (minutes) => {
        set({ pinSessionMinutes: minutes });
        get().saveToCloud();
      },

      startPinSession: () => {
        set({ pinSessionStart: Date.now() });
      },

      isPinSessionValid: () => {
        const { pinSessionStart, pinSessionMinutes } = get();
        if (!pinSessionStart || pinSessionMinutes === 0) return false;
        const elapsed = Date.now() - pinSessionStart;
        return elapsed < pinSessionMinutes * 60 * 1000;
      },

      clearPinSession: () => {
        set({ pinSessionStart: null });
      },
      
      resetToDefaults: () => {
        set({ ...defaultSettings, pinSessionStart: null });
        get().saveToCloud();
      },

      setUserId: (userId) => {
        set({ userId });
        if (userId) {
          get().loadFromCloud();
        }
      },

      loadFromCloud: async () => {
        const { userId } = get();
        if (!userId) return;

        set({ isLoading: true });
        try {
          const docRef = doc(db, SETTINGS_COLLECTION, userId);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const cloudSettings = docSnap.data() as Settings;
            
            set({
              paperStyle: cloudSettings.paperStyle ?? defaultSettings.paperStyle,
              textPaddingTop: cloudSettings.textPaddingTop ?? defaultSettings.textPaddingTop,
              fontSize: cloudSettings.fontSize ?? defaultSettings.fontSize,
              fontFamily: cloudSettings.fontFamily ?? defaultSettings.fontFamily,
              lineOpacity: cloudSettings.lineOpacity ?? defaultSettings.lineOpacity,
              marginLineOpacity: cloudSettings.marginLineOpacity ?? defaultSettings.marginLineOpacity,
              lineSpacing: cloudSettings.lineSpacing ?? defaultSettings.lineSpacing,
              pinCode: cloudSettings.pinCode ?? defaultSettings.pinCode,
              pinSessionMinutes: cloudSettings.pinSessionMinutes ?? defaultSettings.pinSessionMinutes,
              // Clear session on load - user must re-enter PIN
              pinSessionStart: null,
            });
          }
        } catch (error) {
          console.error('Failed to load settings from cloud:', error);
        } finally {
          set({ isLoading: false });
        }
      },

      saveToCloud: async () => {
        const { userId, paperStyle, textPaddingTop, fontSize, fontFamily, lineOpacity, marginLineOpacity, lineSpacing, pinCode, pinSessionMinutes } = get();
        if (!userId) return;

        try {
          const docRef = doc(db, SETTINGS_COLLECTION, userId);
          await setDoc(docRef, {
            paperStyle,
            textPaddingTop,
            fontSize,
            fontFamily,
            lineOpacity,
            marginLineOpacity,
            lineSpacing,
            pinCode,
            pinSessionMinutes,
            updatedAt: new Date(),
          }, { merge: true });
        } catch (error) {
          console.error('Failed to save settings to cloud:', error);
        }
      },
    }),
    {
      name: 'membank-settings',
      partialize: (state) => ({
        paperStyle: state.paperStyle,
        textPaddingTop: state.textPaddingTop,
        fontSize: state.fontSize,
        fontFamily: state.fontFamily,
        lineOpacity: state.lineOpacity,
        marginLineOpacity: state.marginLineOpacity,
        lineSpacing: state.lineSpacing,
        pinCode: state.pinCode,
        pinSessionMinutes: state.pinSessionMinutes,
        // Don't persist pinSessionStart - it should reset on app reload
      }),
    }
  )
);

// Subscribe to real-time updates from Firestore
export const subscribeToSettings = (userId: string) => {
  const docRef = doc(db, SETTINGS_COLLECTION, userId);
  
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const cloudSettings = docSnap.data() as Settings;
      useSettingsStore.setState({
        paperStyle: cloudSettings.paperStyle ?? defaultSettings.paperStyle,
        textPaddingTop: cloudSettings.textPaddingTop ?? defaultSettings.textPaddingTop,
        fontSize: cloudSettings.fontSize ?? defaultSettings.fontSize,
        fontFamily: cloudSettings.fontFamily ?? defaultSettings.fontFamily,
        lineOpacity: cloudSettings.lineOpacity ?? defaultSettings.lineOpacity,
        marginLineOpacity: cloudSettings.marginLineOpacity ?? defaultSettings.marginLineOpacity,
        lineSpacing: cloudSettings.lineSpacing ?? defaultSettings.lineSpacing,
        pinCode: cloudSettings.pinCode ?? defaultSettings.pinCode,
        pinSessionMinutes: cloudSettings.pinSessionMinutes ?? defaultSettings.pinSessionMinutes,
        // Don't change pinSessionStart on sync
      });
    }
  }, (error) => {
    console.error('Settings subscription error:', error);
  });
};
